import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import CustomToast from "../CustomToast";
import {
  ToastContextType,
  ToastProps,
  ToastProviderProps,
  ToastStatus,
} from "./ToastProvider.d";
import { initializeToast } from "../../utils/toast";

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface InternalToast extends ToastProps {
  id: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [currentToast, setCurrentToast] = useState<InternalToast | null>(null);

  const showToast = useCallback((props: ToastProps) => {
    setCurrentToast({
      id: Date.now(),
      status: props.status || "completed",
      title: props.title || "",
      message: props.message || "",
      duration: props.duration || 3000,
      onUndo: props.onUndo,
      onView: props.onView,
      isCloseable: props.isCloseable,
    });
  }, []);

  useEffect(() => {
    initializeToast(showToast);
  }, [showToast]);

  const handleToastClose = useCallback(() => {
    setCurrentToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {currentToast && (
        <CustomToast
          key={currentToast.id}
          status={currentToast.status as ToastStatus}
          title={currentToast.title || ""}
          message={currentToast.message || ""}
          duration={currentToast.duration || 3000}
          onClose={handleToastClose}
          onUndo={currentToast.onUndo}
          onView={currentToast.onView}
          isCloseable={currentToast.isCloseable}
        />
      )}
    </ToastContext.Provider>
  );
};
